/**
 * Helper centralizado para manejo de branch_id
 * Soluciona problemas de sincronización entre localStorage y sessionStorage
 * Optimizado para rendimiento con cache en memoria
 */

// Cache en memoria para evitar lecturas repetidas de storage
let memoryCache = {
  activeBranchId: null,
  timestamp: null,
};

// Tiempo de vida del cache en memoria (ms)
const CACHE_TTL = 5000; // 5 segundos

/**
 * Obtiene el branch_id activo de forma consistente
 * Prioridad: memory cache > sessionStorage > localStorage
 * @returns {string|null} El ID de la sucursal activa
 */
export const getActiveBranchId = () => {
  // Verificar cache en memoria primero (rendimiento)
  const now = Date.now();
  if (memoryCache.activeBranchId && memoryCache.timestamp && 
      (now - memoryCache.timestamp) < CACHE_TTL) {
    return memoryCache.activeBranchId;
  }

  // Leer de storage
  const fromSession = sessionStorage.getItem("activeBranchId");
  const fromLocal = localStorage.getItem("activeBranchId");
  
  // Priorizar sessionStorage (sesión actual), fallback a localStorage
  const value = fromSession || fromLocal || null;
  
  // Actualizar cache
  memoryCache = {
    activeBranchId: value,
    timestamp: now,
  };
  
  return value;
};

/**
 * Establece el branch_id activo
 * @param {string|number} id - ID de la sucursal
 * @param {boolean} persist - Si true, también guarda en localStorage
 */
export const setActiveBranchId = (id, persist = true) => {
  const value = id ? String(id) : null;
  
  // Siempre guardar en sessionStorage (sesión actual)
  if (value) {
    sessionStorage.setItem("activeBranchId", value);
  } else {
    sessionStorage.removeItem("activeBranchId");
  }
  
  // Guardar en localStorage solo si se solicita persistencia
  if (persist && value) {
    localStorage.setItem("activeBranchId", value);
  } else if (!persist) {
    localStorage.removeItem("activeBranchId");
  }
  
  // Actualizar cache en memoria
  memoryCache = {
    activeBranchId: value,
    timestamp: Date.now(),
  };
  
  // Disparar evento para sincronización entre componentes
  window.dispatchEvent(new CustomEvent("branchChanged", { 
    detail: { branchId: value, persist } 
  }));
};

/**
 * Limpia el branch_id activo
 */
export const clearActiveBranchId = () => {
  sessionStorage.removeItem("activeBranchId");
  localStorage.removeItem("activeBranchId");
  memoryCache = { activeBranchId: null, timestamp: null };
  
  window.dispatchEvent(new CustomEvent("branchChanged", { 
    detail: { branchId: null } 
  }));
};

/**
 * Obtiene el branch_id como número entero
 * @returns {number|null}
 */
export const getActiveBranchIdInt = () => {
  const id = getActiveBranchId();
  return id ? parseInt(id, 10) : null;
};

/**
 * Verifica si hay una sucursal activa seleccionada
 * @returns {boolean}
 */
export const hasActiveBranch = () => {
  return !!getActiveBranchId();
};

/**
 * Sincroniza el branch_id entre storages
 * Útil después de login o cambio de sucursal
 */
export const syncBranchId = () => {
  const sessionId = sessionStorage.getItem("activeBranchId");
  const localId = localStorage.getItem("activeBranchId");
  
  // Si hay en session pero no en local, copiar a local
  if (sessionId && !localId) {
    localStorage.setItem("activeBranchId", sessionId);
  }
  // Si hay en local pero no en session, copiar a session
  else if (localId && !sessionId) {
    sessionStorage.setItem("activeBranchId", localId);
  }
  
  // Retornar el valor sincronizado
  return getActiveBranchId();
};

/**
 * Hook para escuchar cambios de sucursal
 * @param {Function} callback - Función a ejecutar cuando cambie la sucursal
 * @returns {Function} Función para remover el listener
 */
export const onBranchChange = (callback) => {
  const handler = (event) => {
    callback(event.detail.branchId, event.detail.persist);
  };
  window.addEventListener("branchChanged", handler);
  return () => window.removeEventListener("branchChanged", handler);
};

export default {
  getActiveBranchId,
  getActiveBranchIdInt,
  setActiveBranchId,
  clearActiveBranchId,
  hasActiveBranch,
  syncBranchId,
  onBranchChange,
};
